import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Heart, Shield } from 'lucide-react';

export const SaderinasanasGredzeniRiga = () => {
  return (
    <>
      <Helmet>
        <title>Saderināšanās gredzeni Rīgā | Pure Atelier Riga</title>
        <meta name="description" content="Ekskluzīvi saderināšanās gredzeni Rīgā. Augstākās kvalitātes dimanti, individuāls dizains, labākās cenas Latvijā. Bezmaksas konsultācija." />
        <meta name="keywords" content="saderināšanās gredzeni, saderināšanās gredzeni Rīgā, gredzeni ar dimantiem, engagement rings Riga" />
        <link rel="canonical" href="https://pureatelier.lv/saderinasnanas-gredzeni-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1616096/pexels-photo-1616096.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Saderināšanās gredzeni Rīgā
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Ekskluzīvi saderināšanās gredzeni ar augstākās kvalitātes dimantiem. 
                Katrs gredzens ir unikāls meistardarbs, radīts īpašiem mirkļiem.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Skatīt kolekciju <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Sveiki!%20Vēlos%20uzzināt%20par%20saderināšanās%20gredzeniem." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp konsultācija
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Sertificēti dimanti", desc: "GIA un IGI sertifikāti" },
                { icon: Award, title: "Augsta kvalitāte", desc: "Roku darbs Latvijā" },
                { icon: Heart, title: "Individuāls dizains", desc: "Pēc jūsu vēlmēm" },
                { icon: Shield, title: "Garantija", desc: "Pilna garantija izstrādājumiem" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Kāpēc izvēlēties Pure Atelier saderināšanās gredzenus?
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier Riga</strong> piedāvā ekskluzīvus saderināšanās gredzenus, kas radīti ar mīlestību un rūpību. 
                  Mūsu kolekcijā atradīsiet gan klasiskos solitaire gredzenus, gan modernākus dizainus ar halo ietvaru.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Mūsu priekšrocības</h3>
                <ul>
                  <li><strong>Labākās cenas Rīgā</strong> — tiešā sadarbība ar dimanta piegādātājiem</li>
                  <li><strong>Individuāla pieeja</strong> — palīdzēsim izvēlēties perfektu gredzenu</li>
                  <li><strong>Ātra izgatavošana</strong> — no 5 darba dienām</li>
                  <li><strong>Bezmaksas pielāgošana</strong> — izmēra maiņa bez maksas</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Populārākie saderināšanās gredzenu stili</h3>
                <p>
                  Mūsu kolekcijā pieejami dažādi stili — no klasiskā <strong>Solitaire</strong> ar vienu dimantu līdz 
                  elegantajiem <strong>Halo</strong> gredzeniem ar mazākiem dimantiem ap galveno akmeni. 
                  Piedāvājam arī <strong>Bezel</strong> ietvaru tiem, kas vēlas drošāku dimanta stiprinājumu.
                </p>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Apskatīt visus gredzenus <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Gatavs izvēlēties saderināšanās gredzenu?</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Sazinieties ar mums WhatsApp, lai saņemtu bezmaksas konsultāciju un palīdzību gredzena izvēlē.
            </p>
            <a href="https://wa.me/37128448991?text=Sveiki!%20Vēlos%20uzzināt%20par%20saderināšanās%20gredzeniem." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> Sazināties WhatsApp
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default SaderinasanasGredzeniRiga;
